import { api } from './api.service';

export interface Product {
  productIdentifier: string;
  name: string;
  brandInfo: {
    brandId: number;
    brandName: string;
  };
  price: number;
  availableToSell: number;
  imageUrl?: string;
  description?: string;
  category?: string;
  currencyCode?: string;
  market?: number;
  discount?: number;
  discountPercentage?: number;
  estimatedTimeOfArrival?: string;
}

export interface ProductDetails extends Product {
  specifications?: Record<string, string>;
  features?: string[];
  imageGallery?: string[];
}

export interface ProductSearchFilters {
  searchText?: string;
  keyword?: string;
  market?: number;
  brand?: string;
  category?: string;
  priceMin?: number;
  priceMax?: number;
  availability?: boolean;
  sortOptions?: string;
}

interface ProductApiResponse {
  productIdentifier: string;
  name: string;
  brandId: number;
  brandName: string;
  price: number;
  availableToSell: number;
  imageUrl?: string;
  description?: string;
  category?: string;
  currencyCode?: string;
  market?: number;
  discount?: number | null;
  discountPercentage?: number | null;
  estimatedTimeOfArrival?: string;
}

interface ProductSearchResponse {
  products: ProductApiResponse[];
  totalCount: number;
}

function mapApiProduct(apiProduct: ProductApiResponse): Product {
  return {
    productIdentifier: apiProduct.productIdentifier,
    name: apiProduct.name,
    brandInfo: {
      brandId: apiProduct.brandId,
      brandName: apiProduct.brandName,
    },
    price: apiProduct.price / 100,
    availableToSell: apiProduct.availableToSell,
    imageUrl: apiProduct.imageUrl,
    description: apiProduct.description,
    category: apiProduct.category,
    currencyCode: apiProduct.currencyCode,
    market: apiProduct.market,
    discount: apiProduct.discount != null ? apiProduct.discount / 100 : undefined,
    discountPercentage: apiProduct.discountPercentage ?? undefined,
    estimatedTimeOfArrival: apiProduct.estimatedTimeOfArrival,
  };
}

export async function searchProducts(
  filters: ProductSearchFilters,
  pageIndex = 0,
  pageSize = 20,
  signal?: AbortSignal,
): Promise<{ products: Product[]; totalCount: number }> {
  const body = {
    searchText: filters.searchText || filters.keyword,
    market: filters.market,
    brand: filters.brand,
    category: filters.category,
    priceMin: filters.priceMin,
    priceMax: filters.priceMax,
    availability: filters.availability,
    sortOptions: filters.sortOptions,
    pageIndex,
    maxResultCount: pageSize,
  };

  const response = await api.post<ProductSearchResponse>('/products/search', body, { signal });

  return {
    products: response.products.map(mapApiProduct),
    totalCount: response.totalCount,
  };
}

export async function getProductDetails(
  brand: number,
  productId: string,
  market = 14,
  signal?: AbortSignal,
): Promise<ProductDetails | null> {
  try {
    const response = await api.get<ProductApiResponse & { specifications?: Record<string, string>; features?: string[]; imageGallery?: string[] }>(
      `/products/${brand}/${encodeURIComponent(productId)}`,
      { market },
      signal,
    );
    return {
      ...mapApiProduct(response),
      specifications: response.specifications,
      features: response.features,
      imageGallery: response.imageGallery,
    };
  } catch (error) {
    if ((error as { status?: number }).status === 404) {
      return null;
    }
    throw error;
  }
}
